'use client';

import { useState, useEffect } from 'react';
import { MessageSquare, FileText, Sparkles, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';

const steps = [
  {
    number: '01',
    icon: MessageSquare,
    title: 'Tell Us About Your Properties',
    description: 'Share your portfolio details, property types, and current pain points. We\'ll assess your needs and complexity level.',
    color: 'from-blue-500 to-blue-600',
  },
  {
    number: '02',
    icon: FileText,
    title: 'We Build Your Custom Plan',
    description: 'Within 48 hours, you\'ll receive a tailored proposal with transparent pricing based on your unit count and complexity.',
    color: 'from-primary-400 to-primary-500',
  },
  {
    number: '03',
    icon: Sparkles,
    title: 'We Handle Everything',
    description: 'From vendor coordination to emergency response, your dedicated team manages it all while you track progress in Axis.',
    color: 'from-emerald-500 to-emerald-600',
  },
];

const HowItWorks = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    const section = document.getElementById('how-it-works');
    if (section) observer.observe(section);

    return () => observer.disconnect();
  }, []);

  return (
    <section id="how-it-works" className="relative py-16 sm:py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gray-50 dark:bg-secondary-950" />

      {/* Decorative line connecting steps */}
      <div className="absolute top-1/2 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent hidden lg:block" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span
            className={cn(
              "inline-block px-4 py-1.5 rounded-full text-sm font-medium mb-4",
              "bg-primary-500/10 text-primary-400 border border-primary-500/20",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
          >
            Simple Process
          </span>
          <h2
            className={cn(
              "font-playfair text-3xl sm:text-4xl md:text-5xl font-bold mb-4",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '100ms' }}
          >
            <span className="text-gray-900 dark:text-secondary-100">How It Works</span>
          </h2>
          <p
            className={cn(
              "max-w-2xl mx-auto text-lg text-gray-500 dark:text-secondary-400",
              "transition-all duration-700 ease-out",
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            )}
            style={{ transitionDelay: '200ms' }}
          >
            Getting started is easy. Three simple steps to transform your facility management.
          </p>
        </div>

        {/* Steps Grid */}
        <div className="grid md:grid-cols-3 gap-5 sm:gap-8 lg:gap-12">
          {steps.map((step, i) => (
            <div
              key={step.number}
              className={cn(
                "relative group",
                "transition-all duration-700 ease-out",
                isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
              )}
              style={{ transitionDelay: `${300 + i * 150}ms` }}
            >
              {/* Step Card */}
              <div
                className={cn(
                  "relative p-5 sm:p-6 md:p-8 rounded-2xl h-full",
                  "bg-white shadow-sm dark:bg-white/[0.02] dark:shadow-none border border-gray-200 dark:border-white/[0.08]",
                  "hover:bg-gray-50 dark:hover:bg-white/[0.04] hover:border-primary-500/30",
                  "transition-all duration-300"
                )}
              >
                {/* Step Number */}
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <div
                    className={cn(
                      "flex items-center justify-center w-11 h-11 sm:w-14 sm:h-14 rounded-xl",
                      "bg-gradient-to-br",
                      step.color
                    )}
                  >
                    <step.icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                  </div>
                  <span className="text-3xl sm:text-5xl font-bold text-gray-200 dark:text-secondary-800 font-playfair">
                    {step.number}
                  </span>
                </div>

                {/* Content */}
                <h3 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-secondary-100 mb-2 sm:mb-3">
                  {step.title}
                </h3>
                <p className="text-gray-500 dark:text-secondary-400 leading-relaxed">
                  {step.description}
                </p>
              </div>

              {/* Arrow between steps (desktop only) */}
              {i < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-6 transform -translate-y-1/2 z-10">
                  <ArrowRight className="w-5 h-5 text-primary-500/40" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* CTA */}
        <div
          className={cn(
            "text-center mt-16",
            "transition-all duration-700 ease-out",
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          )}
          style={{ transitionDelay: '750ms' }}
        >
          <a
            href="#contact"
            className={cn(
              "inline-flex items-center gap-2 px-8 py-4 rounded-xl",
              "bg-primary-500 text-white font-semibold",
              "hover:bg-primary-400 transition-all duration-300",
              "shadow-[0_0_40px_-10px_rgba(59,130,246,0.5)]",
              "hover:shadow-[0_0_60px_-10px_rgba(59,130,246,0.6)]"
            )}
          >
            Start Your Free Consultation
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </div>
    </section>
  );
};

export { HowItWorks };
